/**
 * Team Detail Route
 *
 * Displays comprehensive team metadata with hierarchy visualization
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { createFileRoute, useNavigate, Link, Outlet, useLocation } from '@tanstack/react-router';
import {
  useTeam,
  useTeamHierarchy,
  useServicesByTeam,
  LIST_SERVICE_ASSIGNMENTS,
  GET_PROJECT,
} from '../../graphql/queries';
import { useApolloClient } from '@apollo/client/react';
import { useUsers } from '../../hooks/useUsers';
import {
  DetailSkeleton,
  EmptyState,
  DeleteConfirmationDialog,
  PermissionGate,
} from '../../components/shared';
import { deleteTeam, addTeamMember, removeTeamMember, ApiError } from '../../lib/apiClient';
import type { TeamHierarchyNode, Service, ServiceAssignment, Project } from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import { safeExternalHref } from '../../lib/safeExternalHref';
import '@tylertech/forge/icon';
import '@tylertech/forge/divider';
import './teamId.css';

export const Route = createFileRoute('/teams/$teamId')({
  component: TeamDetailPage,
});

interface TeamProjectsCardProps {
  serviceIds: string[];
}

function TeamProjectsCard({ serviceIds }: TeamProjectsCardProps): JSX.Element {
  const client = useApolloClient();
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);

  const CardElement = 'forge-card' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;

  useEffect(() => {
    if (serviceIds.length === 0) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    Promise.all(
      serviceIds.map((serviceId) =>
        client.query<{ serviceAssignments: ServiceAssignment[] }>({
          query: LIST_SERVICE_ASSIGNMENTS,
          variables: { serviceId },
        })
      )
    )
      .then((results) => {
        const projectIds = [
          ...new Set(
            results
              .flatMap((r) => r.data?.serviceAssignments ?? [])
              .map((a) => a.projectId)
              .filter(Boolean)
          ),
        ];

        if (projectIds.length === 0) {
          if (!cancelled) {
            setProjects([]);
            setLoading(false);
          }
          return Promise.resolve([]);
        }

        return Promise.all(
          projectIds.map((id) =>
            client.query<{ project: Project | null }>({
              query: GET_PROJECT,
              variables: { id },
            })
          )
        );
      })
      .then((projectResults) => {
        if (!cancelled && projectResults.length > 0) {
          setProjects(
            projectResults
              .map((r) => r.data?.project ?? null)
              .filter((p): p is Project => p !== null)
          );
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [client, serviceIds]);

  return (
    <CardElement className="detail-card">
      <div className="card-header">
        <h2 className="forge-typography--headline6">Projects</h2>
        {!loading && <span className="member-count">{projects.length} projects</span>}
      </div>
      <DividerElement />
      {loading ? (
        <p className="forge-typography--body2 team-detail-muted-text">Loading projects...</p>
      ) : projects.length === 0 ? (
        <p className="forge-typography--body2 team-detail-muted-text">
          No projects associated with this team's services.
        </p>
      ) : (
        <div className="project-list">
          {projects.map((project) => (
            <div key={project.entityId} className="project-item">
              <Link
                to="/projects/$projectId"
                params={{ projectId: project.entityId }}
                className="project-item-name"
              >
                {project.name}
              </Link>
              <span className="project-item-status">{project.status.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </div>
      )}
    </CardElement>
  );
}

const MEMBER_SEARCH_DEBOUNCE_MS = 250;

interface TeamMembersPanelProps {
  teamId: string;
  memberIds: string[];
  onMutate: () => Promise<void>;
}

function TeamMembersPanel({ teamId, memberIds, onMutate }: TeamMembersPanelProps): JSX.Element {
  const { users } = useUsers();
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearch, setShowSearch] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [memberError, setMemberError] = useState<string | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [debouncedQuery, setDebouncedQuery] = useState('');

  const IconElement = 'forge-icon' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, MEMBER_SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const memberSet = useMemo(() => new Set(memberIds), [memberIds]);

  const searchResults = useMemo(() => {
    if (!debouncedQuery.trim()) return [];
    const q = debouncedQuery.toLowerCase();
    return users
      .filter(
        (u) =>
          !memberSet.has(u.userId) &&
          (u.name.toLowerCase().includes(q) || u.email.toLowerCase().includes(q))
      )
      .slice(0, 8);
  }, [users, memberSet, debouncedQuery]);

  const handleAdd = async (userId: string): Promise<void> => {
    setPendingId(userId);
    setMemberError(null);
    try {
      await addTeamMember(teamId, userId);
      await onMutate();
      setSearchQuery('');
      setDebouncedQuery('');
      setShowSearch(false);
    } catch {
      setMemberError('Failed to add member. Please try again.');
    } finally {
      setPendingId(null);
    }
  };

  const handleRemove = async (userId: string): Promise<void> => {
    setPendingId(userId);
    setMemberError(null);
    try {
      await removeTeamMember(teamId, userId);
      await onMutate();
    } catch {
      setMemberError('Failed to remove member. Please try again.');
    } finally {
      setPendingId(null);
    }
  };

  return (
    <>
      {memberError && (
        <p className="team-member-error" role="alert">
          {memberError}
        </p>
      )}
      <div className="member-list" data-testid="member-list">
        {memberIds.length === 0 ? (
          <p className="team-members-empty">No members yet — use Add Member to get started.</p>
        ) : (
          memberIds.map((memberId) => (
            <div key={memberId} className="member-item" data-testid={`member-item-${memberId}`}>
              <IconElement name="person" aria-hidden="true" />
              <span className="member-item-id">{memberId}</span>
              <PermissionGate permission="team:update">
                <ForgeButton
                  type="button"
                  variant="text"
                  className="member-remove-btn"
                  disabled={pendingId === memberId}
                  onClick={() => void handleRemove(memberId)}
                  aria-label={`Remove member ${memberId}`}
                  data-testid={`remove-member-${memberId}`}
                >
                  ✕
                </ForgeButton>
              </PermissionGate>
            </div>
          ))
        )}
      </div>

      <PermissionGate permission="team:update">
        {showSearch ? (
          <div className="member-add-section" data-testid="member-add-section">
            <input
              type="text"
              className="member-search-input"
              placeholder="Search by name or email…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              autoFocus
              data-testid="member-search-input"
              aria-label="Search users to add"
            />
            {debouncedQuery.trim() && searchResults.length === 0 && (
              <p className="member-search-empty">No matching users.</p>
            )}
            {searchResults.length > 0 && (
              <ul className="member-search-results" aria-label="Search results">
                {searchResults.map((u) => (
                  <li key={u.userId} className="member-search-result-item">
                    <div className="member-search-user-info">
                      <span className="member-search-user-name">{u.name}</span>
                      <span className="member-search-user-email">{u.email}</span>
                    </div>
                    <ForgeButton
                      type="button"
                      variant="outlined"
                      disabled={pendingId === u.userId}
                      onClick={() => void handleAdd(u.userId)}
                      aria-label={`Add ${u.name}`}
                      data-testid={`add-member-${u.userId}`}
                    >
                      {pendingId === u.userId ? 'Adding…' : 'Add'}
                    </ForgeButton>
                  </li>
                ))}
              </ul>
            )}
            <ForgeButton
              type="button"
              variant="text"
              onClick={() => {
                setShowSearch(false);
                setSearchQuery('');
                setDebouncedQuery('');
              }}
              data-testid="cancel-add-member"
            >
              Cancel
            </ForgeButton>
          </div>
        ) : (
          <ForgeButton
            type="button"
            variant="outlined"
            className="member-add-btn"
            onClick={() => setShowSearch(true)}
            data-testid="add-member-button"
          >
            <IconElement name="person_add" slot="start" aria-hidden="true" />
            Add Member
          </ForgeButton>
        )}
      </PermissionGate>
    </>
  );
}

function TeamDetailPage(): JSX.Element {
  const { teamId } = Route.useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const apolloClient = useApolloClient();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If on a child route (edit), render only the outlet
  const isChildRoute = location.pathname.endsWith('/edit');

  // Fetch team data
  const { data, loading, error: fetchError } = useTeam(teamId);
  const team = data?.team;

  // Fetch team hierarchy
  const { data: hierarchyData } = useTeamHierarchy(teamId, 3);
  const hierarchy = hierarchyData?.teamHierarchy;

  // Fetch team's services (drives TeamProjectsCard's serviceIds prop, and the
  // service count shown below — issue #1255 removed the inline listing in
  // favor of a count + link to the filtered Service Inventory).
  const { data: servicesData, loading: servicesLoading } = useServicesByTeam(teamId);
  const teamServices: Service[] = servicesData?.servicesByTeam?.edges?.map((e) => e.node) ?? [];
  const teamServiceIds = teamServices.map((s) => s.entityId);
  const teamServiceCount = servicesData?.servicesByTeam?.totalCount ?? teamServices.length;

  const CardElement = 'forge-card' as React.ElementType;
  const IconElement = 'forge-icon' as React.ElementType;
  const DividerElement = 'forge-divider' as React.ElementType;
  const ForgeButton = 'forge-button' as React.ElementType;

  if (isChildRoute) {
    return <Outlet />;
  }

  // Loading state
  if (loading) {
    return (
      <div className="team-detail-page">
        <DetailSkeleton cards={6} />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="team-detail-page">
        <EmptyState
          icon="error"
          title="Failed to load team"
          description={fetchError.message}
          actionLabel="Back to Teams"
          onAction={() => navigate({ to: '/teams', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Not found state
  if (!team) {
    return (
      <div className="team-detail-page">
        <EmptyState
          icon="search_off"
          title="Team not found"
          description={`No team found with ID: ${teamId}`}
          actionLabel="Back to Teams"
          onAction={() => navigate({ to: '/teams', search: { page: 1, pageSize: 25 } })}
        />
      </div>
    );
  }

  // Format dates
  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Handle edit action
  const handleEdit = (): void => {
    navigate({ to: `/teams/${team.entityId}/edit` as string });
  };

  // Handle delete action
  const handleDelete = (): void => {
    setDeleteDialogOpen(true);
  };

  // Confirm delete
  const handleConfirmDelete = async (): Promise<void> => {
    setIsDeleting(true);
    setError(null);

    try {
      // Soft delete by default
      await deleteTeam(teamId, false);

      // Refetch GraphQL data to update cache
      await apolloClient.refetchQueries({
        include: ['ListTeams'],
      });

      // Navigate back to teams list
      navigate({ to: '/teams', search: { page: 1, pageSize: 25 } });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
      setIsDeleting(false);
    }
  };

  // Render hierarchy tree
  const renderHierarchyNode = (node: TeamHierarchyNode, isRoot: boolean = false): JSX.Element => {
    return (
      <div className={isRoot ? 'hierarchy-root' : 'hierarchy-child'}>
        <div className="hierarchy-node">
          <div className="hierarchy-node-name">
            {node.team.entityId === teamId ? (
              <strong>{node.team.name}</strong>
            ) : (
              <Link to="/teams/$teamId" params={{ teamId: node.team.entityId }}>
                {node.team.name}
              </Link>
            )}
          </div>
          <div className="hierarchy-node-meta">
            <span>{node.team.type}</span>
            <span>•</span>
            <span>{node.team.memberIds.length} members</span>
          </div>
        </div>
        {node.children.length > 0 && (
          <div className="hierarchy-children">
            {node.children.map((child, index) => (
              <div key={child.team.entityId || index}>{renderHierarchyNode(child)}</div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="team-detail-page">
      {/* Header */}
      <div className="team-detail-header">
        <div>
          <ForgeButton
            type="button"
            variant="text"
            onClick={() => navigate({ to: '/teams', search: { page: 1, pageSize: 25 } })}
          >
            <IconElement name="arrow_back" aria-hidden="true" />
            Back to Teams
          </ForgeButton>
          <h1 className="forge-typography--headline4">{team.name}</h1>
          <p className="forge-typography--body2 page-header-subtitle">
            {team.description || 'No description provided'}
          </p>
        </div>
        <div className="team-detail-actions">
          <ForgeButton
            type="button"
            variant="outlined"
            onClick={() => navigate({ to: '/teams/$teamId/trends', params: { teamId } })}
          >
            Maturity Trends
          </ForgeButton>
          <PermissionGate permission="team:update">
            <ForgeButton type="button" variant="raised" onClick={handleEdit}>
              <IconElement name="edit" aria-hidden="true" />
              Edit
            </ForgeButton>
          </PermissionGate>
          <PermissionGate permission="team:delete">
            <ForgeButton type="button" variant="outlined" onClick={handleDelete}>
              <IconElement name="delete" aria-hidden="true" />
              Delete
            </ForgeButton>
          </PermissionGate>
        </div>
      </div>

      <div className="team-detail-content">
        {/* Overview Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Overview</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Team ID</span>
              <span className="detail-value">{team.entityId}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Type</span>
              <span className="detail-value detail-value--capitalize">{team.type}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Team Lead</span>
              <span className="detail-value">{team.leadUserId}</span>
            </div>
          </div>
        </CardElement>

        {/* Services Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Services</h2>
            {!servicesLoading && <span className="member-count">{teamServiceCount} services</span>}
          </div>
          <DividerElement />
          {servicesLoading ? (
            <p className="forge-typography--body2 team-detail-muted-text">Loading services...</p>
          ) : teamServiceCount === 0 ? (
            <p className="forge-typography--body2 team-detail-muted-text">
              No services owned by this team.
            </p>
          ) : (
            <Link
              to="/services"
              search={{
                page: 1,
                pageSize: 25,
                search: '',
                lifecycleStatus: '',
                tier: '',
                tagId: '',
                teamId,
                technologyId: '',
              }}
              className="team-services-view-link"
              data-testid="view-services-link"
            >
              View Services
            </Link>
          )}
        </CardElement>

        {/* Projects Card */}
        <TeamProjectsCard serviceIds={teamServiceIds} />

        {/* Team Members Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Team Members</h2>
            <span className="member-count">{team.memberIds.length} members</span>
          </div>
          <DividerElement />
          <TeamMembersPanel
            teamId={teamId}
            memberIds={team.memberIds}
            onMutate={async () => {
              await apolloClient.refetchQueries({ include: ['GetTeam'] });
            }}
          />
        </CardElement>

        {/* Responsibilities Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Responsibilities</h2>
          </div>
          <DividerElement />
          <div className="responsibility-list">
            {team.responsibilities.map((resp, index) => (
              <div key={index} className="responsibility-item">
                <IconElement name="check_circle" aria-hidden="true" />
                <span>{resp}</span>
              </div>
            ))}
          </div>
        </CardElement>

        {/* Contact Information Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Contact Information</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Email</span>
              <span className="detail-value">
                <a href={`mailto:${team.contactEmail}`}>{team.contactEmail}</a>
              </span>
            </div>
            {safeExternalHref(team.teamsChannel) && (
              <div className="detail-row">
                <span className="detail-label">Teams Channel</span>
                <span className="detail-value">
                  <a
                    href={safeExternalHref(team.teamsChannel) ?? undefined}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Open in Teams
                  </a>
                </span>
              </div>
            )}
          </div>
        </CardElement>

        {/* Team Hierarchy Card */}
        {hierarchy && (
          <CardElement className="detail-card">
            <div className="card-header">
              <h2 className="forge-typography--headline6">Team Hierarchy</h2>
            </div>
            <DividerElement />
            <div className="hierarchy-tree">{renderHierarchyNode(hierarchy, true)}</div>
          </CardElement>
        )}

        {/* Metadata Card */}
        <CardElement className="detail-card">
          <div className="card-header">
            <h2 className="forge-typography--headline6">Metadata</h2>
          </div>
          <DividerElement />
          <div className="detail-grid">
            <div className="detail-row">
              <span className="detail-label">Created</span>
              <span className="detail-value">
                {formatDate(team._createdAt)} by {team._createdBy}
              </span>
            </div>
            <div className="detail-row">
              <span className="detail-label">Last Updated</span>
              <span className="detail-value">
                {formatDate(team._updatedAt)} by {team._updatedBy}
              </span>
            </div>
          </div>
        </CardElement>
      </div>

      {/* Delete Confirmation Dialog */}
      {deleteDialogOpen && (
        <DeleteConfirmationDialog
          open={deleteDialogOpen}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteDialogOpen(false)}
          title="Delete Team"
          message="Are you sure you want to delete this team?"
          warning="This action will soft delete the team. It can be recovered by an administrator."
          itemName={team.name}
          isDeleting={isDeleting}
        />
      )}

      {/* Error Banner */}
      {error && <div className="detail-error-toast">{error}</div>}
    </div>
  );
}

export { TeamDetailPage, TeamMembersPanel };
